import { useMemo } from 'react';
import { isExpandedItemsByKey } from '@client/hooks/use-expand-elements.hooks';
import { useSelectedVersion } from '@client/store/ui';
import { useElementImports } from './use-element-imports.hook';
import { useCandidateSearchFilter } from './use-candidate-search.hook';
import {
  useImportedElements,
  type ImportedElementsByRowId,
} from './candidate-status.helpers';
import type {
  CandidateRow,
  ProjectElementImport,
} from '@shared/models/element-import.model';
import {
  buildImportTabVisibleRowIds,
  CSV_IMPORT_EXPAND_PREFIX,
  getImportsNewestFirst,
  restrictImportNavIdsToVisibleCandidates,
} from './import-list-navigation.helpers';
import { useVisibleListRowIdsSnapshot } from './list-selection.store';

export type ImportListKeyboardNavigationContext = {
  visibleRowIds: string[];
  visibleImports: ProjectElementImport[];
  rowsByImport: Record<string, CandidateRow[]>;
  importedElements: ImportedElementsByRowId;
};

/**
 * Import-list keyboard navigation inputs aligned with {@link CandidateRowsTable}.
 */
export const useImportListKeyboardNavigationContext = (
  projectId: number,
): ImportListKeyboardNavigationContext => {
  const importsState = useElementImports(projectId);
  const searchFilter = useCandidateSearchFilter(projectId);
  const version = useSelectedVersion();
  const { ids: visibleCandidateIds, reported: visibleCandidatesReported } =
    useVisibleListRowIdsSnapshot();

  const importedElements = useImportedElements(version);

  return useMemo(() => {
    if (importsState.status !== 'ready') {
      return {
        visibleRowIds: [],
        visibleImports: [],
        rowsByImport: {},
        importedElements,
      };
    }

    const { importsById, rowsByImport } = importsState.data;
    const sortedImports = getImportsNewestFirst(
      importsById,
      version?.imports?.importIds,
    );
    const visibleImports = sortedImports;
    const builtVisibleRowIds = buildImportTabVisibleRowIds({
      visibleImports,
      rowsByImport,
      searchIsActive: searchFilter.isActive,
      rowMatchesSearch: searchFilter.matches,
      isImportExpanded: (importId) =>
        isExpandedItemsByKey(CSV_IMPORT_EXPAND_PREFIX, importId),
      searchScopeImportId: searchFilter.scopeImportId,
    });
    const visibleRowIds = visibleCandidatesReported
      ? restrictImportNavIdsToVisibleCandidates(
          builtVisibleRowIds,
          visibleCandidateIds,
          visibleImports,
        )
      : builtVisibleRowIds;

    return {
      visibleRowIds,
      visibleImports,
      rowsByImport,
      importedElements,
    };
  }, [
    importsState,
    searchFilter,
    importedElements,
    version?.imports?.importIds,
    visibleCandidateIds,
    visibleCandidatesReported,
  ]);
};
